import type { MaybeRefOrGetter } from 'vue';
import type { FieldSelection, GlobalFieldId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { findCollectionFieldById, getRelationPrimitiveFields, idFieldSelectionToFieldSelection, isSingleRelationField, mergeFieldSelections, resolveFieldDisplayFieldSelection } from '#layers/data-model/public';
import { useFieldSelectionContexts } from './use-field-selection-contexts';

/**
 * Compute precise FieldSelection for a set of visible field IDs on a collection.
 *
 * For each field, delegates to `resolveFieldDisplayFieldSelection` which handles
 * both primitive and relation fields, including display block `fields()` resolution.
 */
export function useFieldSelectionForFieldIds(
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | undefined>,
	visibleFieldIds: MaybeRefOrGetter<GlobalFieldId[]>,
) {
	const { fieldSelectionCtx, idCtx } = useFieldSelectionContexts();

	return computed<FieldSelection>(() => {
		const col = toValue(collection);
		const fieldIds = toValue(visibleFieldIds);
		if (!col || fieldIds.length === 0) return { fields: ['*'] };

		const columnSelections: FieldSelection[] = fieldIds.map((fieldId) => {
			const field = findCollectionFieldById(col, fieldId);

			if (!field) return { fields: [] };

			const idSelection = resolveFieldDisplayFieldSelection(field, col, fieldSelectionCtx);
			const displaySelection = idFieldSelectionToFieldSelection(idSelection, idCtx);
			const relationBackingFields = isSingleRelationField(field)
				? getRelationPrimitiveFields(field).fields.map((primitiveField) => primitiveField.apiName)
				: [];

			return mergeFieldSelections(displaySelection, { fields: relationBackingFields });
		});

		return mergeFieldSelections(...columnSelections);
	});
}
